import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, GameController, Globe, Star, Plus, GraduationCap } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublisherPage() {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [publisher, setPublisher] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [featuredGames, setFeaturedGames] = useState([]);
  const [loading, setLoading] = useState(true);

  // If publisherId param, show detail. Otherwise show list.
  const isDetail = !!publisherId;

  useEffect(() => {
    if (isDetail) {
      fetchPublisherDetail();
    } else {
      fetchList();
    }
  }, [publisherId]);

  const fetchPublisherDetail = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/publishers/${publisherId}`, { withCredentials: true });
      setPublisher(data);
    } catch (err) { toast.error('Failed to load publisher'); }
    finally { setLoading(false); }
  };

  const fetchList = async () => {
    try {
      const [pubRes, fgRes] = await Promise.all([
        axios.get(`${API_URL}/api/publishers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/featured-games`, { withCredentials: true }),
      ]);
      setPublishers(pubRes.data);
      setFeaturedGames(fgRes.data);
    } catch (err) { toast.error('Failed to load publishers'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  // Detail view
  if (isDetail && publisher) {
    return (
      <div className="app-container" data-testid="publisher-detail-page">
        <header className="p-6 pb-3">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
              <ArrowLeft size={20} weight="bold" className="text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                <BookOpen size={22} weight="duotone" className="text-[#9D4CDD]" />
                {publisher.name}
                {publisher.verified && <Star size={14} weight="fill" className="text-[#FACC15]" />}
              </h1>
              <p className="text-[#71717A] text-xs">{publisher.description}</p>
            </div>
          </div>
          {publisher.website && (
            <a href={publisher.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#00F0FF] text-xs mb-3">
              <Globe size={12} /> {publisher.website}
            </a>
          )}
        </header>

        <div className="px-6 pb-32 space-y-4">
          {/* Featured Games */}
          {publisher.featured_games?.length > 0 && (
            <div>
              <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <GameController size={16} weight="duotone" className="text-[#FACC15]" />
                Featured Games
              </h3>
              <div className="space-y-2">
                {publisher.featured_games.map((g, i) => (
                  <motion.div key={g.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="p-3 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`featured-game-${i}`}>
                    <div className="flex items-center justify-between">
                      <h4 className="text-white text-sm font-medium">{g.game_name}</h4>
                      {g.promoted && <span className="text-[8px] px-2 py-0.5 bg-[#FACC15] text-[#0A0A12] rounded-full font-bold">PROMOTED</span>}
                    </div>
                    {g.description && <p className="text-[#A1A1AA] text-[10px] mt-1">{g.description}</p>}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Tutorials */}
          {publisher.tutorials?.length > 0 && (
            <div>
              <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <GraduationCap size={16} weight="duotone" className="text-[#00F0FF]" />
                Tutorials
              </h3>
              <div className="space-y-2">
                {publisher.tutorials.map((t, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD]">{t.game_name}</span>
                      <span className="text-[10px] text-[#71717A]">{t.section}</span>
                    </div>
                    <h4 className="text-white text-xs font-medium">{t.title}</h4>
                    <p className="text-[#A1A1AA] text-[10px] mt-1 line-clamp-3">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!publisher.featured_games?.length && !publisher.tutorials?.length) && (
            <div className="text-center py-10">
              <BookOpen size={32} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
              <p className="text-[#71717A] text-sm">No content yet</p>
            </div>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  // List view
  return (
    <div className="app-container" data-testid="publisher-list-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Publishers</h1>
            <p className="text-[#71717A] text-xs">Game publishers & featured content</p>
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Featured Games Section */}
        {featuredGames.length > 0 && (
          <div>
            <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
              <Star size={16} weight="fill" className="text-[#FACC15]" />
              Featured Games
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-none">
              {featuredGames.map((g, i) => (
                <div key={g.id || i} className="min-w-[140px] p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex-shrink-0" data-testid={`featured-game-card-${i}`}>
                  <h4 className="text-white text-xs font-medium truncate">{g.game_name}</h4>
                  {g.publisher_name && <p className="text-[#9D4CDD] text-[9px]">by {g.publisher_name}</p>}
                  {g.promoted && <span className="text-[7px] px-1.5 py-0.5 bg-[#FACC15] text-[#0A0A12] rounded-full font-bold mt-1 inline-block">PROMOTED</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Publishers List */}
        <div>
          <h3 className="text-white text-sm font-semibold mb-2">All Publishers</h3>
          {publishers.length > 0 ? (
            <div className="space-y-2">
              {publishers.map((p, i) => (
                <motion.button key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/publishers/${p.id}`)}
                  className="w-full text-left p-4 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-[#9D4CDD]/30 transition-all"
                  data-testid={`publisher-card-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center">
                      <BookOpen size={18} weight="duotone" className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium flex items-center gap-1 truncate">
                        {p.name}
                        {p.verified && <Star size={10} weight="fill" className="text-[#FACC15]" />}
                      </h4>
                      {p.description && <p className="text-[#71717A] text-[10px] truncate">{p.description}</p>}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#71717A] py-6 text-sm">No publishers yet</p>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
